import { saveAccountImport,recordRejectedImport } from '../services/account-import';
import { useRef, useState } from 'react';
import { parseRideFile } from '../domain/ride-import';
import type { RideImportChoice, XmlRideFormat } from '../domain/ride-import-choice';
import type { Ride } from '../domain/ride';

export interface RideImportState {
  readonly kind: 'idle' | 'loading' | 'local' | 'saved' | 'error';
  readonly message: string;
}

/** Shares import and track selection between the studio and design preview. */
export function useRideImport(onImported: (ride: Ride) => void) {
  const [state, setState] = useState<RideImportState>({ kind: 'idle', message: '' });
  const [tracks, setTracks] = useState<readonly RideImportChoice[] | null>(null);
  const [choiceFormat, setChoiceFormat] = useState<XmlRideFormat>('gpx');
  const busy = useRef(false);
  const previous = useRef(state);
  const pendingFile = useRef<File | null>(null);
  const load = (ride: Ride, warning: string) => {
    onImported(ride);
    setTracks(null);
    setState({ kind: 'local', message: `${ride.sourceFileName ?? ride.title} · loaded locally${warning}` });
  };
  const save = async (file: File, ride: Ride, warnings: readonly string[] = []) => {
    const warning = warnings.length ? `. ${warnings.join(' ')}` : '';
    const stored = await saveAccountImport(file, ride);
    load(ride, warning);
    pendingFile.current = null;
    if(stored)setState({kind:'saved',message:`${file.name} · saved to your account${warning}`});
  };
  const accept = async (ride: Ride) => {
    if(busy.current || !pendingFile.current)return;
    busy.current=true;
    setState({kind:'loading',message:`Reading ${pendingFile.current.name}`});
    try { await save(pendingFile.current,ride,tracks?.find(track => track.ride === ride)?.warnings); }
    catch(error) { setState({kind:'error',message:error instanceof Error?error.message:'This trace file could not be read.'}); }
    finally { busy.current=false; }
  };
  const importFile = async (file: File) => {
    if (busy.current || tracks) return;
    busy.current = true;
    previous.current = state;
    setState({ kind: 'loading', message: `Reading ${file.name}` });
    let parsed=false;
    try {
      const result = await parseRideFile(file);
      parsed=true;
      if (result.kind === 'ride') { await save(file,result.ride,result.warnings); }
      else {
        pendingFile.current = file;
        setChoiceFormat(result.format);
        setTracks(result.tracks);
        setState({ kind: 'idle', message: result.format === 'tcx' ? 'Choose a recorded activity.' : 'Choose a recorded track.' });
      }
    } catch (error) {
      if(!parsed)await recordRejectedImport(file.name).catch(()=>undefined);
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'This trace file could not be read.' });
    } finally { busy.current = false; }
  };
  const cancel = () => { if(busy.current)return; pendingFile.current=null; setTracks(null); setState(previous.current); };
  const reset = () => { if(busy.current)return; pendingFile.current=null; setTracks(null); setState({ kind: 'idle', message: '' }); };
  return { state, tracks, choiceFormat, importFile, accept, cancel, reset };
}
